import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, X, Minus, Plus, Trash2, Lock } from "lucide-react";
import { useCart, setQty, removeItem, cartTotal, cartCount, checkoutUrl } from "@/lib/shopifyCart";
import { formatMoney } from "@/components/shop/ShopifyProducts";

/**
 * Cart drawer for the Shopify "My Products" section. Floating button appears
 * once the cart has items; checkout redirects to Shopify's secure checkout
 * via a cart permalink.
 */
export default function CartDrawer({ checkoutBase, currency }) {
  const items = useCart();
  const [open, setOpen] = useState(false);
  const count = cartCount(items);

  return (
    <div data-testid="cart-shell" data-checkout-base={checkoutBase || "none"} data-count={count}>
      <AnimatePresence>
        {count > 0 && !open && (
          <motion.button
            key="cart-btn"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 18 }}
            onClick={() => setOpen(true)}
            data-testid="cart-open-button"
            aria-label="Open cart"
            className="fixed bottom-6 right-6 z-[140] w-14 h-14 rounded-full bg-slate-900 text-white flex items-center justify-center shadow-2xl hover:scale-105 active:scale-95 transition-transform"
          >
            <ShoppingCart className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-primary text-[10px] font-bold flex items-center justify-center" data-testid="cart-count">
              {count}
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <>
            <motion.div
              key="cart-backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="fixed inset-0 bg-black/40 z-[145]"
              data-testid="cart-backdrop"
            />
            <motion.aside
              key="cart-drawer"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-white z-[150] flex flex-col shadow-2xl"
              data-testid="cart-drawer"
            >
              <div className="p-5 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <img src="/shop-images/aveno-icon.png" alt="" className="w-7 h-7 object-contain" />
                  <h2 className="font-display text-xl">Your cart</h2>
                </div>
                <button onClick={() => setOpen(false)} aria-label="Close cart" data-testid="cart-close"
                  className="p-2 text-slate-500 hover:text-slate-900 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                {items.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-12" data-testid="cart-empty">Your cart is empty.</p>
                )}
                {items.map((i) => (
                  <div key={i.variantId} className="flex gap-4 border border-slate-100 rounded-xl p-3" data-testid="cart-item">
                    {i.image && (
                      <img src={i.image} alt="" className="w-16 h-16 rounded-lg object-cover bg-slate-50 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">{i.title}</p>
                      {i.variantTitle && <p className="text-xs text-slate-500">{i.variantTitle}</p>}
                      {i.customization && (
                        <p className="text-xs text-primary mt-0.5" data-testid="cart-item-custom">
                          {i.customization.design}{i.customization.text ? ` · "${i.customization.text}"` : ""}{i.customization.artworkUrl ? " · own artwork" : ""}
                        </p>
                      )}
                      <p className="text-sm text-slate-700 mt-1">{formatMoney(i.price, currency)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button onClick={() => setQty(i.variantId, i.qty - 1)} aria-label="Decrease quantity" data-testid="cart-qty-minus"
                          className="w-7 h-7 border border-slate-200 rounded-md flex items-center justify-center hover:border-primary transition-colors">
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="text-sm w-5 text-center" data-testid="cart-qty">{i.qty}</span>
                        <button onClick={() => setQty(i.variantId, i.qty + 1)} aria-label="Increase quantity" data-testid="cart-qty-plus"
                          className="w-7 h-7 border border-slate-200 rounded-md flex items-center justify-center hover:border-primary transition-colors">
                          <Plus className="w-3 h-3" />
                        </button>
                        <button onClick={() => removeItem(i.variantId)} aria-label="Remove item" data-testid="cart-remove"
                          className="ml-auto p-1.5 text-slate-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {items.length > 0 && (
                <div className="p-5 border-t border-slate-100 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Subtotal</span>
                    <span className="font-semibold text-slate-900" data-testid="cart-subtotal">{formatMoney(cartTotal(items), currency)}</span>
                  </div>
                  <button
                    onClick={() => { window.location.href = checkoutUrl(items, checkoutBase); }}
                    data-testid="cart-checkout-button"
                    className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white font-semibold py-3.5 rounded-full hover:bg-primary/90 active:scale-[0.98] transition"
                  >
                    <Lock className="w-4 h-4" /> Checkout securely with Shopify
                  </button>
                  <p className="text-[11px] text-slate-400 text-center">Taxes and shipping calculated at checkout. Sold by AVENO.</p>
                </div>
              )}
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
